/**
 * WeatherModule — weather VFX driven by SkyState.weather (Graphics G4).
 *
 * Reads the world's `SkyState.weather` → the server-authoritative `WeatherProfile` →
 * drives: a GPU rain/snow particle system (reusing the engine `GpuParticleEmitter`,
 * repositioned around the camera), the shared foliage-wind uniform, and the shared
 * surface-wetness uniform (lerped). Particle density scales by the quality tier. Live-ops
 * `setWeather` flips the weather server-side and the VFX follows. Mirrors LightingModule.
 *
 * Task 3 (Phase C): each frame the module also resolves the biome×drift weather at the
 * player position, lerps toward it, and drives the engine clouds + the existing VFX from
 * the smoothly-interpolated result. The getCloudCoverage() hook is the engine's read-point:
 * CloudRuntimeSystem (RenderSub/5) auto-reads it from the 'Weather' module each frame.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import type { App, IModule } from 'enginex';
import { GpuParticleEmitter, CpuParticleAdapter } from 'enginex/particles/gpu';
import { formationForWeather } from 'enginex/render/clouds';
import type { DbConnection } from '../module_bindings/index.js';
import { disposeLater } from '../lib/gpu_dispose.js';
import { sfxAt, sfxLater, sfxLoop } from '../lib/sfx.js';
import { RAIN_NEAR, RAIN_MID, RAIN_SPLASH, SNOW_NEAR, SNOW_MID, DUST_NEAR, DUST_FAR, DUST_SALT, VOLUME, emissionRate, splashSeatY, type FxConfig } from '../lib/weather_fx.js';
import { groundYIn } from '../lib/grounding.js';
import { waterStillAt } from '../lib/water_fx.js';
import { mx_fractal_noise_float, positionWorld, cameraPosition, normalize, vec3, float, smoothstep, uniform as tslUniform } from 'three/tsl';
import { applySoftParticles } from 'enginex/vfx/SoftParticleModifier.js';
import { weatherAudioMix, thunderFor, stormStrike, WEATHER_LOOP_KEYS, type WeatherAudioMix } from '../lib/weather_audio.js';
import { lightningAmbientShare } from '../lib/vfx/lightning.js'; // fire realism R7 (fire agent)
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { shelterExposure, precipSlant, gateCpuEmitters, cpuDropGateLive } from '../lib/rain_shelter.js';
import { driveWetClock } from '../lib/wet_drive.js'; // weather surfaces R3: THE ONE WET CLOCK
import { enterables } from '../lib/building_interiors.js';
import { activePreset, activeTier } from '../lib/quality_tiers.js';
import { cloudLight, cloudSunSteps, driveCloudUplight } from '../lib/sky/cloud_light.js';
import { lightFrame, exposureState } from '../lib/photometry.js';
import { cloudGenus } from '../lib/stage4_fx.js';
import { heapSkyFor, type HeapSky, type HeapSheets } from '../lib/sky/cloud_formation.js';
import { cloudSheetsMode } from '../lib/sky/cloud_sheets.js';
import { cloudAirLayers, cloudAirOn, type CloudAirLayers } from '../lib/sky/cloud_air.js';

/** THE CLOUDS' AIR: Atlas's atmosphere as two layers (constant — the atmosphere is one config), and no air (the kill switch). */
const CLOUD_AIR: CloudAirLayers = cloudAirLayers();
const CLOUD_AIR_NONE: CloudAirLayers = { low: { r: 0, g: 0, b: 0 }, lowScaleM: 1200, high: { r: 0, g: 0, b: 0 }, highScaleM: 8000 };
import { CLEAR_AIR_HAZE } from '../lib/sky/fog_sky.js';
/** The dust shells' radiance per unit of the global light (scene units): 0.85 reproduces the old noon tan (2026-09-25). */
const DUST_SHELL_GAIN = 0.85;
import { windVector, windPrevDt } from '../lib/wind.js';
import { setWetness, getWetness, setSnowiness, setDust } from '../lib/wetness.js';
import { resolveWeather, pickWeatherOverride, meteoAt, lerpResolved, type ResolvedWeather, type Meteo } from '../lib/weather.js';
import { uNightFactor, currentTod } from './SkyModule.js';

// ── Layered precipitation (coverage overhaul 2026-08-06; recipes moved to
// lib/weather_fx.ts on 2026-09-11) ──
// Industry practice (Lagarde "Water drop", Tatarchuk ToyShop, MS realtime-rain)
// is LAYERED camera-linked volumes: a dense NEAR volume of thin streaks for
// the "inside the storm" read, a sparse MID volume of larger, fainter quads
// that sells precipitation out to ~200 m, and a FAR field carried by fog/haze
// (resolveWeather adds a precip haze term to fogDensity). Both volumes follow
// the camera; the weather FIELD (meteoAt) decides where they are active.
// ⚠ startSpeed is NEGATIVE for precipitation: the kernel's emission direction
// for box shapes is the constant +Y — a positive speed launched every drop
// upward first (2026-08-20). Rain now SPAWNS at terminal velocity (Gunn &
// Kinzer) with gravity off, as a STREAK sprite; snow at 1–2 m/s riding curl
// noise; a sandstorm's dust streams past along the wind from boxes spawned
// UPWIND. The emitter is the engine's GPU-compute one on WebGPU and the CPU
// adapter on WebGL 2 (the waterfall's law: their spawn spaces differ — the
// weather volumes follow the camera in RENDER space either way, so both take
// the camera's world position).
type Emitter = GpuParticleEmitter | CpuParticleAdapter;

export class WeatherModule implements IModule {
  readonly name = 'Weather';
  private app!: App;
  private scene!: THREE.Scene;
  private near: Emitter | null = null;
  private mid: Emitter | null = null;
  private splash: Emitter | null = null;
  /** The sandstorm's saltation layer (grains hopping along the ground). */
  private salt: Emitter | null = null;
  private streakTex: { fall: THREE.Texture | null; wind: THREE.Texture | null } = { fall: null, wind: null };
  /** The DUST HAZE: three inside-out shells of wind-scrolled noise around the camera (a windy volumetric-like fog). */
  private dustShells: THREE.Mesh[] = [];
  private readonly uDust = tslUniform(0);
  private readonly uDustWind = tslUniform(new THREE.Vector3(1, 0, 0));
  private readonly uDustSun = tslUniform(1);
  private readonly fxPos = new THREE.Vector3();
  private readonly fxDir = new THREE.Vector3();
  private readonly fxUp = new THREE.Vector3(0, 1, 0);
  private readonly fxQuat = new THREE.Quaternion();
  private readonly fxScale = new THREE.Vector3(1, 1, 1);
  private audioAt = 0; // wall-clock ms of the last audio re-mix (dt is clamped under slow frames)
  private forceFlash = false; // probe lever: one lightning flash + its thunder now
  private stormWallS = 0; // the wall second the lightning schedule was last sampled at (stormStrike's `prevS`)
  private audioMix: WeatherAudioMix | null = null;
  private readonly audioOn: Record<string, boolean> = {};
  private thunder = { fired: 0, lastKey: '', lastDelayS: 0 };
  private emitterKind = 'none';
  /** The WEATHER's precipitation kind, before the tier's particle cap turns the emitter off (the wet clock reads it). */
  private weatherKind = 'none';
  private lastMeteo: Meteo | null = null; // debug/HUD readout (temperature, RH, dew point…)
  private targetWetness = 0;
  private windStrength = 0;
  private density = 0;
  private elapsed = 0;
  private subscribed = false;
  private readonly windDir = new THREE.Vector2(0.8, 0.2).normalize();
  private snowiness = 0;
  private flash: THREE.AmbientLight | null = null;
  private resolved: ResolvedWeather | null = null; // smoothly-lerped current weather from biome×drift
  private lerpedCoverage = 0.4;                     // returned to CloudRuntimeSystem each frame
  private lastFormationKey = '';                    // quantized regime latch — setFormation only on change
  private heapSky: HeapSky | null = null;           // the heap clouds for the resolved weather (lib/sky/cloud_formation)
  private heapSkyInput = '';
  private readonly camWorld = new THREE.Vector3();  // scratch: camera world position (emitter follow)
  private readonly camAbs = new THREE.Vector3();    // scratch: the camera in ABSOLUTE coords (scene children)
  private cloudsMod: { renderer: { setDensityScale(v: number): void; setSunColor(c: { r: number; g: number; b: number }): void; uniforms: { sunIlluminance: { value: number }; ambientBottom: { value: number }; ambientTop: { value: number } } }; lowRenderer?: unknown; meshField?: unknown } | undefined;
  private debugWeatherOverride: string | null = null; // dev/admin Local override (debug menu); wins over the live-ops override
  private cachedSeed: string | null = null;           // commons terrainSeed is constant — resolve once, never per-frame

  /** The engine CloudRuntimeSystem (RenderSub/5) auto-reads this each frame. */
  getCloudCoverage(): number { return this.lerpedCoverage; }
  /** Stage 4: what is falling and how hard (rain ripples read it each frame). */
  getPrecip(): { kind: string; density: number } { return { kind: this.emitterKind, density: this.density }; }
  /** Blown-dust amount 0..1 at the player (the sandstorm field), for the light volume. */
  getDust(): number { return this.resolved?.dust ?? 0; }
  /** The prevailing wind direction (unit XZ) — the high cirrus combs along it (SkyModule, 2026-09-25). */
  windDirection(): { x: number; z: number } { return { x: this.windDir.x, z: this.windDir.y }; }
  /** The weather the LIGHT sees (LightingModule's photometry, 2026-09-25): the lerped deck cover + density, the dust dimming, the precipitation. */
  getLightWeather(): { cover: number; density: number; sunDim: number; precip: number } {
    const r = this.resolved;
    return r ? { cover: r.cloudCoverage, density: r.cloudDensity, sunDim: r.sunDim, precip: r.particleKind === 'rain' || r.particleKind === 'snow' ? r.particleDensity : 0 } : { cover: 0, density: 1, sunDim: 0, precip: 0 };
  }

  /** Debug menu (Local mode): force the resolved weather at the player. null = clear the override (back to biome×drift / live-ops). */
  setDebugWeather(w: string | null, snap = false): void {
    this.debugWeatherOverride = w;
    // Probe lever (2026-09-25, the lighting probe): jump straight to the pinned weather instead of the ~2 s lerp —
    // under SwiftShader a frame is seconds long and the lerp outlived the socket's reload window.
    if (snap) { this.snapWeather = true; this.resolveTarget = null; }
  }
  private snapWeather = false;
  /** Probe/debug: pin the snow accumulation (0..1) regardless of the emitter; null releases it. */
  private debugSnow: number | null = null;
  setDebugSnow(v: number | null): void { this.debugSnow = v === null ? null : Math.max(0, Math.min(1, v)); }
  /** Inspection hook for the debug panel readout. */
  get debugWeather(): string | null { return this.debugWeatherOverride; }
  /** The heap clouds for the resolved weather, recomputed only when its inputs move a per cent (the rule allocates). */
  private heapSkyNow(r: ResolvedWeather): HeapSky {
    const precip = r.particleKind === 'none' ? 0 : r.particleDensity;
    const p = activePreset();
    // The layer clouds are sheets wherever the high étage draws (the tier has clouds, the sky is not the flat one), unless switched back.
    const sheets = (p.highClouds ?? !!p.cloudsEnabled) && p.skyQuality !== 'flat' && cloudSheetsMode() === 'sheets';
    // What the sheets show right now (SkyModule writes it at RenderSub/5, before this tick): the heaps answer to it.
    // null before the first lit frame — the cover alone decides until then.
    const sky = sheets ? ((this.app?.getModule('Sky') as { cloudSheetSpec?: HeapSheets | null } | undefined)?.cloudSheetSpec ?? null) : null;
    const key = `${Math.round(r.cloudCoverage * 100)}:${Math.round(precip * 100)}:${sheets ? 1 : 0}`
      + (sky ? `:${sky.low.genus}:${Math.round(sky.low.cover * 100)}:${sky.mid.genus}:${Math.round(sky.mid.cover * 100)}` : ':-');
    if (!this.heapSky || key !== this.heapSkyInput) {
      this.heapSkyInput = key;
      this.heapSky = heapSkyFor(r.cloudCoverage, precip, sheets, (c, pr, alts) => {
        // The legacy formation (sheets off): the engine's, less the mesh "cirrus" where the old single sheet draws it.
        const f = formationForWeather(c, pr, alts);
        if ((p.highClouds ?? !!p.cloudsEnabled) && p.skyQuality !== 'flat' && f.layers.some((l) => l.kind !== 'cirrus')) f.layers = f.layers.filter((l) => l.kind !== 'cirrus');
        return f;
      }, sky);
    }
    return this.heapSky;
  }
  /**
   * Probe / debug (`__cloudSky`, the cloud-realism work 2026-10-07): pin the CLOUD sky over whatever the weather resolved —
   * the cover (0..1), the density multiplier, and optionally the mesh field's formation — so a probe can photograph one
   * cloud type at a chosen cover. null releases it. The light follows (getLightWeather reads the pinned cover).
   */
  private debugSky: { cover?: number; density?: number; formation?: { layers: unknown[] } } | null = null;
  setDebugSky(s: { cover?: number; density?: number; formation?: { layers: unknown[] } } | null): void {
    this.debugSky = s; this.lastFormationKey = ''; this.snapWeather = true; this.resolveTarget = null;
  }

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    // Lightning flash light (idle at 0 — costs nothing until a storm).
    this.flash = new THREE.AmbientLight(0xd8e6ff, 0);
    this.scene.add(this.flash);
    app.world.addSystem(makeSystem('WeatherUpdate', Phase.RenderSub, 22, (dt) => this.update(dt)));
    // Diagnostic surface: the meteo readout + emitter state at the player.
    (window as unknown as Record<string, unknown>).__weather = () => ({
      meteo: this.lastMeteo,
      kind: this.emitterKind,
      density: this.density,
      particles: this.activeParticles,
      layers: { near: this.near !== null, mid: this.mid !== null, splash: this.splash !== null, salt: this.salt !== null, dustShells: this.dustShells.length },
      backend: this.near?.backend ?? null,
      // THE SPLASH SEAT (physics follow-up A): the absolute height the splash slab sits at, the ground / water it read,
      // and the camera's absolute height — a probe judges "on the ground", not "1.55 m under the camera".
      splash: { seatY: this.seatAt.y, ground: this.seatAt.ground, water: this.seatAt.water, camY: this.camAbs.y, worldY: this.splash ? this.splash.worldMatrix.elements[13] : null },
      resolved: this.resolved ? { cloudCoverage: this.resolved.cloudCoverage, cloudDensity: this.resolved.cloudDensity, cloudTintHex: this.resolved.cloudTintHex, fogDensity: this.resolved.fogDensity, dust: this.resolved.dust, sunDim: this.resolved.sunDim, fogTintHex: this.resolved.fogTintHex, windStrength: this.resolved.windStrength, wetness: this.resolved.wetness } : null,
      audio: this.audioMix,
      thunder: { ...this.thunder },
      override: this.debugWeatherOverride,
      // The resolver's TARGET (instantaneous — the lerp toward it is slow under a slow renderer).
      target: this.resolveTarget ? { kind: this.resolveTarget.particleKind, density: this.resolveTarget.particleDensity, cloudCoverage: this.resolveTarget.cloudCoverage, fogDensity: this.resolveTarget.fogDensity, dust: this.resolveTarget.dust, sunDim: this.resolveTarget.sunDim, windStrength: this.resolveTarget.windStrength, fogTintHex: this.resolveTarget.fogTintHex } : null,
    });
    (window as unknown as Record<string, unknown>).__weatherFlash = () => { this.forceFlash = true; return this.thunder.fired; };
    // …and its clock: `__cloudAge(60)` lets a minute pass for every heap cloud (a pinned sky is born as wisps).
    (window as unknown as Record<string, unknown>).__cloudAge = (seconds = 60) => {
      const mf = (this.cloudsMod as { meshField?: { ageBy?(s: number): void; activeCount?: number } } | undefined)?.meshField;
      mf?.ageBy?.(seconds);
      return mf?.activeCount ?? null;
    };
    // The cloud sky's probe lever: pin { cover, density, formation } (null releases); returns the live cloud state.
    (window as unknown as Record<string, unknown>).__cloudSky = (spec?: { cover?: number; density?: number; formation?: { layers: unknown[] } } | null) => {
      if (spec !== undefined) this.setDebugSky(spec);
      const mf = (this.cloudsMod as { meshField?: { activeCount?: number; formation?: unknown } } | undefined)?.meshField;
      return { pinned: this.debugSky, cover: this.resolved?.cloudCoverage ?? null, density: this.resolved?.cloudDensity ?? null, regime: cloudGenus.regime, volumes: mf?.activeCount ?? null, formation: mf?.formation ?? null };
    };
    bus.on('atlas:account-ready', () => { this.subscribe(); this.applyFromSky(); });
    bus.on('atlas:world-cleared', () => { this.cachedSeed = null; }); // a re-seeded Commons must not keep the old field
    for (const ev of ['atlas:environment-updated', 'atlas:world-entered'] as const) bus.on(ev, () => this.applyFromSky());
  }

  private subscribe(): void {
    if (this.subscribed) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.subscribed = true;
    conn.subscriptionBuilder()
      .onApplied(() => this.applyFromSky())
      .subscribe(['SELECT * FROM sky_state', 'SELECT * FROM weather_profile', 'SELECT * FROM world']);
  }

  private commonsWeather(conn: DbConnection): string | null {
    for (const w of conn.db.world.iter()) {
      if (w.kind === 'commons') { const s = conn.db.skyState.worldId.find(w.worldId); return s ? s.weather : null; }
    }
    return null;
  }

  private weatherProfile(conn: DbConnection, weather: string) {
    return [...conn.db.weatherProfile.iter()].find((p) => p.weather === weather);
  }

  private applyFromSky(): void {
    const conn = atlas.conn;
    if (!conn) return;
    // Weather is COMMONS weather. In space there is no weather at all, and
    // other worlds carry their own skies — anywhere but the commons the VFX
    // clear (snow was falling in the sector: the camera-following emitter
    // rode the ship, 2026-07-30).
    if (atlas.currentWorldId === null || atlas.currentWorldId !== atlas.commonsWorldId) {
      this.targetWetness = 0;
      this.windStrength = 0;
      this.density = 0;
      this.setEmitter('none');
      // The resolved Commons weather must not follow the player off-world:
      // update() read `resolved.dust` every frame, so a sandstorm's sand
      // sheets and its overcast deck rode along into a personal world until
      // the next Commons entry (2026-09-12).
      this.resolved = null;
      this.lerpedCoverage = 0.4;
      return;
    }
    const w = this.commonsWeather(conn);
    if (w) this.applyWeather(w, conn);
  }

  /** Drive the VFX from a weather kind + its server-authoritative profile. */
  applyWeather(weather: string, conn: DbConnection): void {
    const wp = this.weatherProfile(conn, weather);
    if (!wp) return;
    this.targetWetness = wp.wetness;
    this.windStrength = wp.windStrength;
    this.density = wp.particleDensity;
    this.setEmitter(wp.particleKind);
  }

  private setEmitter(kind: string): void {
    // particleCap 0 (potato) = NO emitter at all. The cap only scales the
    // emission RATE — without this the 8,192-instance emitter mesh + its
    // per-frame compute/draw stayed resident with zero live particles (F2 in
    // the 2026-07-27 per-tier census). Kind flips back automatically when the
    // cap returns >0 (weather refresh re-calls setEmitter with the real kind).
    // coordinator QA (2026-09-30): the wet clock reads the WEATHER's kind — minimal and potato ship the clock on with no
    // particles, and with the capped kind their ground never got wet in the rain
    this.weatherKind = kind;
    if (activePreset().particleCap <= 0) kind = 'none';
    if (kind !== this.emitterKind) {
      this.disposeEmitters();
      this.emitterKind = kind;
      if (kind === 'rain' || kind === 'snow' || kind === 'dust') {
        try {
          this.near = this.mk(kind === 'rain' ? RAIN_NEAR : kind === 'snow' ? SNOW_NEAR : DUST_NEAR);
          this.scene.add(this.near.mesh);
          this.near.play();
          this.mid = this.mk(kind === 'rain' ? RAIN_MID : kind === 'snow' ? SNOW_MID : DUST_FAR);
          this.scene.add(this.mid.mesh);
          this.mid.play();
          if (kind === 'rain') {
            // Impact splashes — rain only (snow lands silently, dust never lands).
            this.splash = this.mk(RAIN_SPLASH);
            this.scene.add(this.splash.mesh);
            this.splash.play();
          }
          if (kind === 'dust') {
            // The sand that comes off the GROUND: the saltation layer, plus the haze shells.
            this.salt = this.mk(DUST_SALT);
            this.scene.add(this.salt.mesh);
            this.salt.play();
            this.buildDustShells();
          }
        } catch (e) { console.warn('[g4] particles unavailable on this backend', e); this.disposeEmitters(); }
      }
    }
    this.refreshRates();
  }

  /** The emission rates from the current kind + density (+ the indoor fallback). */
  private refreshRates(): void {
    const mul = this.indoorRateMul();
    this.lastIndoorMul = mul;
    const cap = activePreset().particleCap * mul;
    const k = this.emitterKind as 'rain' | 'snow' | 'dust';
    if (this.near) this.near.setEmissionRate(emissionRate(k, 'near', this.density, cap));
    if (this.mid) this.mid.setEmissionRate(emissionRate(k, 'mid', this.density, cap));
    if (this.splash) this.splash.setEmissionRate(emissionRate('rain', 'splash', this.density, cap));
    if (this.salt) this.salt.setEmissionRate(emissionRate('dust', 'salt', this.density, cap));
  }

  /** THE ROOF-INDOORS LAW's fallback (2026-09-22): the WebGPU emitter's size
   *  gate reads the roof map and stops each drop at the ceiling while the
   *  street outside the panes keeps pouring; a backend or tier WITHOUT the
   *  map has no per-drop test, so inside a building the whole emission stops
   *  (the report's "minimal emergency fallback" — never the primary path). */
  private indoorRateMul(): number {
    // weather surfaces R3: the WebGL2 CPU drops are gated too once the CPU copy of the roof has landed
    const gated = !!activePreset().rainShelter && (this.app.renderer.isWebGPU || cpuDropGateLive());
    if (gated) return 1;
    const id = atlas.interiorId;
    return id !== null && enterables.has(id) ? 0 : 1;
  }
  private lastIndoorMul = 1;

  /**
   * The dust haze: three inside-out shells (10 / 28 / 70 m) around the camera
   * whose opacity is the dust × wind-scrolled fractal noise — a cheap stand-in
   * for volumetric fog that MOVES (a sandstorm is not a still haze), soft-faded
   * where a shell meets the ground so nothing cuts. Nearer terrain occludes the
   * farther shells, so the haze thickens with distance like fog does.
   */
  private buildDustShells(): void {
    if (this.dustShells.length) return;
    const shells: Array<[number, number, number]> = [[10, 0.16, 2.2], [28, 0.22, 1.1], [70, 0.32, 0.5]];
    for (const [radius, alpha, freq] of shells) {
      const mat = new THREE.MeshBasicNodeMaterial();
      const dir = normalize(positionWorld.sub(cameraPosition));
      const n = mx_fractal_noise_float(vec3(dir.x.mul(freq * 3).add(this.uDustWind.x.mul(-0.4)), dir.y.mul(freq * 3), dir.z.mul(freq * 3).add(this.uDustWind.z.mul(-0.4))).add(this.uDustWind.mul(0.001)), 3, 2.1, 0.55, 1);
      const body = smoothstep(float(-0.35), float(0.55), n);
      mat.colorNode = vec3(0.78, 0.6, 0.38).mul(this.uDustSun);
      mat.opacityNode = this.uDust.mul(float(alpha)).mul(float(0.45).add(body.mul(0.9)));
      mat.transparent = true; mat.depthWrite = false; mat.side = THREE.BackSide; mat.fog = false;
      markMrtOverlay(mat); // haze around the camera: the terrain behind it keeps its normal (AO)
      try { applySoftParticles(mat as never, { softDistance: Math.max(3, radius * 0.35), cameraFar: 120_000 }); } catch { /* best-effort */ }
      const mesh = new THREE.Mesh(new THREE.SphereGeometry(radius, 28, 18), mat);
      mesh.name = `WeatherDustShell${radius}`;
      mesh.frustumCulled = false;
      mesh.renderOrder = 6;
      this.scene.add(mesh);
      this.dustShells.push(mesh);
    }
  }

  private disposeDustShells(): void {
    for (const m of this.dustShells) { this.scene.remove(m); disposeLater(m.geometry); disposeLater(m.material as THREE.Material); }
    this.dustShells = [];
  }

  /** An emitter from a pure recipe: the box as a Vector3, the CPU adapter's noise mirrored from the curl, the rain streak sprite. */
  private mk(cfg: FxConfig): Emitter {
    const c: Record<string, unknown> = {
      ...cfg,
      shape: { type: 'box', halfExtents: new THREE.Vector3(cfg.shape.halfExtents.x, cfg.shape.halfExtents.y, cfg.shape.halfExtents.z) },
      blending: THREE.NormalBlending,
    };
    if (cfg.forces.curl) c.noise = { ...cfg.forces.curl };
    // Render wave 1 (2026-09-17): a drop under a roof collapses to nothing — the
    // material's vertex-stage size gate reads the roof map (WebGPU emitter only;
    // the CPU adapter has no hook, the ground still dries there).
    if (activePreset().rainShelter && this.app.renderer.isWebGPU) c.sizeGate = (p: unknown) => shelterExposure(p as never);
    if (cfg.streak) {
      c.texture = this.streakTexture(cfg.streak);
      // A streak must follow the particle's VELOCITY on screen, not the
      // camera's up axis (2026-09-14, "when I look up ... each dropplet
      // sideways"): a plain billboard pins the sprite's +Y to SCREEN up, which
      // is only the fall direction while the camera faces the horizon. Sand
      // streaks get a shorter stretch — they are gusts, not falling lines.
      c.renderMode = 'stretched';
      c.velocityStretch = cfg.streak === 'fall' ? 2.6 : 1.8;
    }
    const e = this.app.renderer.isWebGPU ? new GpuParticleEmitter(this.app.renderer as never, c as never) : new CpuParticleAdapter(c as never);
    // Both renderers output PREMULTIPLIED colour: blend as such, or every soft
    // edge is multiplied by alpha twice and the sprite wears a dark rim (the
    // "shadow around the rain" of 2026-09-11 — a big streak shows what a
    // 6 cm dot hid).
    (e.mesh.material as THREE.Material).premultipliedAlpha = true;
    return e;
  }

  /**
   * A streak sprite. The long axis is ALWAYS the sprite's +Y, because the quad
   * is velocity-aligned (renderMode 'stretched') and the shader maps +Y onto
   * the particle's direction of travel — so falling rain reads vertical and
   * blown sand reads along the wind from the same convention, at any camera
   * angle. 'fall' is a thin raindrop streak (Garg & Nayar) with soft ends;
   * 'wind' is a fatter, softer sand streak.
   *
   * (Before 2026-09-14 'wind' was drawn horizontally and both were pinned to
   * the screen axes, which is why looking up tipped the rain over.)
   */
  private streakTexture(kind: 'fall' | 'wind'): THREE.Texture {
    const have = this.streakTex[kind];
    if (have) return have;
    const canvas = document.createElement('canvas');
    canvas.width = 128; canvas.height = 128;
    const g = canvas.getContext('2d') as CanvasRenderingContext2D;
    g.clearRect(0, 0, 128, 128);
    const along = g.createLinearGradient(0, 0, 0, 128); // ALWAYS along +Y — see the note above
    along.addColorStop(0, 'rgba(255,255,255,0)'); along.addColorStop(0.18, 'rgba(255,255,255,0.95)');
    along.addColorStop(0.82, 'rgba(255,255,255,0.95)'); along.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = along;
    const w = kind === 'fall' ? 7 : 12; // core width in px of 128
    g.fillRect(64 - w / 2, 0, w, 128);
    const across = g.createLinearGradient(64 - w, 0, 64 + w, 0);
    across.addColorStop(0, 'rgba(255,255,255,0)'); across.addColorStop(0.5, 'rgba(255,255,255,1)'); across.addColorStop(1, 'rgba(255,255,255,0)');
    g.globalCompositeOperation = 'destination-in';
    g.fillStyle = across;
    g.fillRect(64 - w, 0, 2 * w, 128);
    const t = new THREE.CanvasTexture(canvas);
    t.needsUpdate = true;
    this.streakTex[kind] = t;
    return t;
  }

  private disposeEmitters(): void {
    // Deferred: a rain→snow swap or a tier drop frees three GPU particle systems
    // in the frame whose submit may still reference them.
    if (this.near) { this.scene.remove(this.near.mesh); disposeLater(this.near); this.near = null; }
    if (this.mid) { this.scene.remove(this.mid.mesh); disposeLater(this.mid); this.mid = null; }
    if (this.splash) { this.scene.remove(this.splash.mesh); disposeLater(this.splash); this.splash = null; }
    if (this.salt) { this.scene.remove(this.salt.mesh); disposeLater(this.salt); this.salt = null; }
    this.disposeDustShells();
  }

  private rate(): number { return this.density * 4000 * activePreset().particleCap; }

  private resolveAccum = 0;
  private resolveTarget: ReturnType<typeof resolveWeather> | null = null;

  private update(dt: number): void {
    if (this.hasEmitter && this.indoorRateMul() !== this.lastIndoorMul) this.refreshRates(); // a door crossing on a backend without the roof gate
    this.elapsed += dt;

    // Resolve the METEO weather at the player, lerp toward it (no popping), drive consumers.
    // COMMONS-ONLY: weather is commons weather (space and other worlds carry
    // their own skies — applyFromSky clears the VFX there). Without this gate
    // the per-frame block re-armed the emitters from the local meteo EVERY
    // frame everywhere: it resurrected the 2026-07-30 "snow rode the ship"
    // bug in space/personal worlds, and during boot (commonsWorldId not yet
    // replicated) it churned create/dispose against applyFromSky's clear on
    // every sky tick.
    const conn = atlas.conn;
    const inCommons = atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId;
    if (conn && inCommons) {
      // The meteo field + the weather resolve are noise/terrain evaluations that
      // change over MINUTES — re-resolve at ~5 Hz (perf sweep 2026-08-22: this ran
      // every render frame, scanning two tables and sampling the field 60×/s);
      // the 2 s lerp below still runs per frame, so nothing pops.
      this.resolveAccum += dt;
      if (this.resolveTarget === null || this.resolveAccum >= 0.2) {
        this.resolveAccum = 0;
        const seed = this.commonsSeed(conn);                // world.terrainSeed for kind==='commons'
        const override = pickWeatherOverride(this.debugWeatherOverride, this.commonsWeatherOverride(conn)); // Local debug override wins over live-ops
        // SHARED clock: wall-clock seconds, not module uptime — every client must
        // compute the same advected fronts (uptime gave each client its own
        // weather phase; clock skew of seconds is invisible at 6 km cells).
        const now = Date.now() / 1000;
        const tod = currentTod();
        this.lastMeteo = meteoAt(seed, atlas.localX, atlas.localZ, now, tod);
        this.resolveTarget = resolveWeather(seed, atlas.localX, atlas.localZ, now, override, tod);
        const ds = this.debugSky; // the probe's pin (setDebugSky)
        if (ds) {
          if (typeof ds.cover === 'number' && Number.isFinite(ds.cover)) this.resolveTarget.cloudCoverage = Math.max(0, Math.min(1, ds.cover));
          if (typeof ds.density === 'number' && Number.isFinite(ds.density)) this.resolveTarget.cloudDensity = Math.max(0, ds.density);
        }
      }
      const target = this.resolveTarget;
      const k = Math.min(1, dt * 0.5);                    // ~2 s time-constant lerp (no biome-boundary pop)
      this.resolved = this.snapWeather ? target : lerpResolved(this.resolved ?? target, target, k);
      this.snapWeather = false;
      const r = this.resolved;
      this.lerpedCoverage = r.cloudCoverage;
      // Re-source the existing VFX state from the resolved (continuous) weather.
      // applyWeather/applyFromSky may also set these; the per-frame block wins — intended.
      this.targetWetness = r.wetness;
      this.windStrength = r.windStrength;
      this.density = r.particleDensity;
      this.setEmitter(r.particleKind);
      // Drive the engine clouds: density + night-lighting + tint.
      // Coverage flows automatically: CloudRuntimeSystem reads getCloudCoverage() above.
      type CloudRendererLike = { setDensityScale(v: number): void; setSunColor(c: { r: number; g: number; b: number }): void; uniforms: { sunIlluminance: { value: number }; ambientBottom: { value: number }; ambientTop: { value: number } } };
      const cloud = (this.cloudsMod ??= this.app.getModule('Clouds') as never) as undefined | { renderer: CloudRendererLike; lowRenderer?: CloudRendererLike | null; meshField?: CloudRendererLike | null }; // cached: getModule is a linear scan, this runs every frame
      if (cloud) {
        const nf = (uNightFactor as unknown as { value: number }).value; // 0 = full day → 1 = full night
        const tint = hexToRgb01(r.cloudTintHex);
        // Day: sun-lit bright clouds. Night: faint ambient-lit silhouettes (not
        // black holes in the stars — and NOT glowing grey either). These are
        // LINEAR radiance values: the engine ambient is no longer attenuated by
        // the Beer-Powder sun term, so the old night values (0.04–0.10 ≈ 0.35
        // in sRGB) read as bright grey glow at midnight — the same
        // daylight-authored-value-at-night family as the fog bug. Verified by
        // the cloud harness night gate (added luminance < 8/255 over the
        // empty night sky). The LOW layer and the MESH field get the identical
        // ramp — they render in the same sky and would otherwise glow at night.
        // THE CLOUDS' AIR (lib/sky/cloud_air): the heaps are seen through Atlas's own clear air, per channel — blue-grey
        // shade a few km out, cream and low in contrast at the horizon. The scene fog reaches them only by what the
        // WEATHER adds: where the aerial froxels draw the clear air the fog is already that excess; elsewhere its clear-air
        // floor is named so it is not counted twice. (`atlas:cloud-air` = 'off' — the clouds as before.)
        type Rgb3 = { r: number; g: number; b: number };
        const airField = cloud.meshField as unknown as { setAir?(lo: Rgb3, loScale: number, hi: Rgb3, hiScale: number): void; setHazeClearAir?(d: number): void } | undefined;
        if (airField?.setAir) {
          const on = cloudAirOn();
          const froxels = !!(this.app?.getModule('Atmosphere') as { aerialOn?: boolean } | undefined)?.aerialOn;
          const L = on ? CLOUD_AIR : CLOUD_AIR_NONE;
          airField.setAir(L.low, L.lowScaleM, L.high, L.highScaleM);
          airField.setHazeClearAir?.(on && !froxels ? CLEAR_AIR_HAZE * activePreset().fogScale : 0);
        }
        // Heaps under an overcast deck (scud under rain, the last cumulus under a closing stratocumulus) get no direct sun.
        const physLow = cloudLight(tint, r.sunDim, 'low', this.heapSkyNow(r).underDeck);
        // THE HIGH DECK'S SUN (cloud_light): the 3.8–9.6 km ray-march deck keeps the sun ~1.2° longer than the low ones.
        const physHigh = cloudLight(tint, r.sunDim, 'high');
        for (const rend of [cloud.renderer, cloud.lowRenderer, cloud.meshField]) {
          if (!rend) continue;
          rend.setDensityScale(r.cloudDensity);
          const phys = rend === cloud.renderer ? physHigh : physLow;
          if (phys) {
            // CLOUD LIGHTING v2 (2026-09-25, realism §8 R6a): the sun on the cloud is the KEY's own light at the
            // cloud base (the atmosphere's transmittance → pink and orange undersides at sunset), the ambient is
            // the drawn sky's (tops) and the lit ground's (bases), all in the exposure curve's scene units; the
            // biome/weather tint is the droplets' ALBEDO, never the sun's colour.
            rend.setSunColor(phys.sunColor);
            rend.uniforms.sunIlluminance.value = phys.sunIlluminance;
            rend.uniforms.ambientTop.value = phys.ambientTop;
            rend.uniforms.ambientBottom.value = phys.ambientBottom;
            const v2 = rend as unknown as { setAlbedo?: (c: { r: number; g: number; b: number }) => void; setAmbientColors?: (t: { r: number; g: number; b: number }, b: { r: number; g: number; b: number }) => void; setMultiScatter?: (w: number) => void; setSunSteps?: (n: number) => void };
            v2.setAlbedo?.(tint);
            v2.setAmbientColors?.(phys.topColor, phys.bottomColor);
            v2.setMultiScatter?.(1);
            v2.setSunSteps?.(cloudSunSteps(activeTier()));
          } else {
            rend.setSunColor(tint);
            // A dust wall dims the sun (sunDim) — the clouds go dull tan, not sunlit.
            rend.uniforms.sunIlluminance.value  = (5    * (1 - nf) + 0.02  * nf) * (1 - r.sunDim);
            rend.uniforms.ambientBottom.value   = 0.3  * (1 - nf) + 0.004 * nf;
            rend.uniforms.ambientTop.value      = 1.0  * (1 - nf) + 0.012 * nf;
          }
        }
        // THE UPLIGHT (the night-sky realism wave, 2026-10-06): a lit town's light on the cloud BASES above it — the
        // provider (the contract's glow at each cloud) follows the live sources, the scale the exposure and the night.
        driveCloudUplight(cloud as never, nf);
        // FORMATION (2026-10-07, the cloud-realism work — lib/sky/cloud_formation): the mesh field carries the HEAP
        // clouds only (cumulus humilis → mediocris → congestus; scud under a rain deck); the layer clouds are the sky's
        // sheets. Quantised by the rule's own key so the field re-streams on a REAL change, never per lerp tick.
        const mf = (cloud as { meshField?: { setFormation?(f: unknown): void } }).meshField;
        if (mf?.setFormation) {
          const heap = this.heapSkyNow(r);
          cloudGenus.regime = heap.regime; // the cloud shadows darken by genus (stage4_fx, 2026-09-25)
          const pinned = this.debugSky?.formation ?? null;
          const fKey = pinned ? `pin:${JSON.stringify(pinned)}` : heap.key;
          if (fKey !== this.lastFormationKey) {
            this.lastFormationKey = fKey;
            mf.setFormation(pinned ?? { layers: heap.formation.layers.map((l) => ({ ...l })) });
          }
        }
      }
    }

    // Off the Commons (personal worlds, the sector) no town lights a cloud: the uplight goes to 0 (2026-10-06).
    if (!(conn && inCommons) && this.cloudsMod) driveCloudUplight(this.cloudsMod as never, 0);
    // Foliage/particle wind blows along the meteo prevailing direction.
    if (this.lastMeteo) this.windDir.set(this.lastMeteo.windDirX, this.lastMeteo.windDirZ).normalize();
    windVector.set(this.windDir.x, this.windDir.y, this.windStrength, this.elapsed);
    (windPrevDt as unknown as { value: number }).value = Math.min(0.1, Math.max(1 / 240, dt)); // the groundcover's motion vectors: last frame's sway
    // weather surfaces R3: THE ONE WET CLOCK (lib/wet_state.ts) — wetting by rain × exposure, drying by evaporation over
    // minutes; the flag off (or `atlas:wet` = 'off') keeps this lerp exactly as it was.
    if (!driveWetClock(dt, {
      kind: this.weatherKind, density: this.density, windX: this.windDir.x, windZ: this.windDir.y, windStrength: this.windStrength,
      tempC: this.lastMeteo?.temperatureC ?? null, rh: this.lastMeteo?.humidity01 ?? null,
      nightFactor: (uNightFactor as unknown as { value: number }).value, cover: this.lerpedCoverage,
      targetWetness: this.targetWetness, inCommons: inCommons && !atlas.spaceFlight,
    })) {
      const w = getWetness();
      setWetness(w + (this.targetWetness - w) * Math.min(1, dt * 0.6)); // lerp toward the target
    }
    // The sandstorm on the GROUND and in the AIR: the ground's sand sheets and
    // the haze shells read the same dust + wind; the shells take the sun.
    const dustNow = this.resolved?.dust ?? 0;
    setDust(dustNow, this.windDir.x, this.windDir.y);
    (this.uDust as unknown as { value: number }).value = dustNow;
    (this.uDustWind as unknown as { value: THREE.Vector3 }).value.set(this.windDir.x * this.elapsed * 0.35, 0, this.windDir.y * this.elapsed * 0.35);
    // The haze shells are an UNLIT medium: their radiance is the light that reaches them — the global
    // illuminance in the exposure curve's scene units (2026-09-25); the old 0.25 night floor glowed at midnight.
    const lm = lightFrame.model;
    (this.uDustSun as unknown as { value: number }).value = lm
      ? DUST_SHELL_GAIN * (lm.globalLux * exposureState.luxToScene) / Math.PI
      : 0.25 + 0.75 * (1 - (uNightFactor as unknown as { value: number }).value);
    // Snow accumulation eases in while snowing (and melts ~4x slower).
    const snowTarget = this.emitterKind === 'snow' ? Math.min(1, this.density * 1.2) : 0;
    this.snowiness += (snowTarget - this.snowiness) * Math.min(1, dt * (snowTarget > this.snowiness ? 0.08 : 0.02));
    setSnowiness(this.debugSnow ?? this.snowiness); // the debug pin wins (probes)
    // Storm lightning: deterministic flashes from the shared clock when the
    // sky is at storm intensity — an ambient pulse ~120 ms, a few per minute.
    const storming = this.emitterKind === 'rain' && this.density > 0.85;
    if (this.flash) {
      // THE SHARED STRIKE: the schedule and the hash ride the WALL clock — `elapsed` is this client's own time in the world, so no two players saw the same bolt
      const wallS = Date.now() / 1000;
      const strike = stormStrike(wallS, this.stormWallS || wallS);
      this.stormWallS = wallS;
      const inFlash = (storming && strike.inFlash) || this.forceFlash;
      this.forceFlash = false;
      // Thunder rides the flash's rising edge (cooldown dedups within a window);
      // a slight rate wobble keeps repeated claps from sounding identical.
      if (inFlash && this.flash.intensity < 0.5) {
        // Three claps, picked and DELAYED by the strike's distance (3 s per km;
        // a close strike is the sharp crack) — the old one clap at 2 s fixed.
        const h = strike.h;
        const t = thunderFor(h, this.indoors());
        this.thunder = { fired: this.thunder.fired + 1, lastKey: t.key, lastDelayS: t.delayS };
        // THE CLAP HAS A DIRECTION (2026-09-20): the same hash that sets the strike's distance sets its bearing, and the
        // thunder arrives from there, from above. Placed at a NOMINAL 60 m — direction only: the clip was already chosen
        // by distance (close crack / far rumble), so real metres would darken it a second time. And it is scheduled in
        // THIS world: a clap up to 10.5 s late used to follow you through a world change (a bare setTimeout).
        const bearing = ((h * 12.9898) % 1) * Math.PI * 2;
        bus.emit('atlas:lightning-strike', { bearing, km: 0.3 + h * 3.2, h }); // fire realism R7 (fire agent, 2026-09-25): AtlasLightningModule draws the bolt / the in-cloud flash + the directional flash from this strike
        const from = { x: atlas.localX + Math.sin(bearing) * 52, y: atlas.localY + 30, z: atlas.localZ + Math.cos(bearing) * 52 };
        sfxLater(t.delayS * 1000, () => sfxAt(t.key, from, { gain: t.gain, rate: t.rate, cooldownMs: 400, caption: 'Thunder', occlude: false }));
      }
      this.flash.intensity += ((inFlash ? 2.6 * lightningAmbientShare() : 0) - this.flash.intensity) * Math.min(1, dt * 18); // R7: the ambient share drops to ~30 % once the directional flash + the bolt exist
    }
    // THE SOUND OF THE WEATHER (2026-09-11): the Higgsfield beds crossfaded by
    // what falls, how hard and how windy (lib/weather_audio.ts), muffled by
    // walls, silent under water. Re-mixed 4×/s, ramped over 0.8 s.
    const nowMs = performance.now();
    if (nowMs - this.audioAt >= 250) {
      this.audioAt = nowMs;
      const mix = weatherAudioMix({
        kind: (this.emitterKind === 'rain' || this.emitterKind === 'snow' || this.emitterKind === 'dust') ? this.emitterKind : 'none',
        density: this.density, wind: this.windStrength, indoors: this.indoors(), submerged: !!atlas.submerged,
      });
      this.audioMix = mix;
      for (const key of WEATHER_LOOP_KEYS) {
        const g = mix[key];
        // The last call sends exactly 0 (2026-09-23): a final gain between 0.001 and 0.003 is not silent to the
        // runtime, so the bed kept a voice — up to five 30 s beds pinned — at an inaudible level for the session.
        if (g > 0.003 || this.audioOn[key]) { sfxLoop(key, 'weather').set(g > 0.003 ? g : 0, 1, 0.8); this.audioOn[key] = g > 0.003; }
      }
    }
    if (this.near || this.mid) {
      let cam: THREE.Camera | null = null;
      try { cam = this.app.renderer.cameras.main; } catch { /* no camera yet */ }
      if (cam) {
        // Both volumes follow the camera (the standard open-world trick — you
        // can never outrun the storm you are inside of); the mid volume sits
        // higher so its longer-lived sheets fill the 50–200 m band.
        // WORLD position, not cam.position: the avatar camera is a CHILD of
        // the body rig, so its local position is a constant (0, eye, 0) —
        // feeding it here pinned both precipitation volumes at the render
        // origin forever. Rain looked right near the rebase anchor (where
        // every probe stood) and silently stayed behind once the player
        // walked a few hundred metres ("no rain around me inside a rain
        // region"). getWorldPosition is the rig-composed render-space point,
        // which is the emitters' spawn-origin space (kernels ignore the mesh
        // transform).
        cam.getWorldPosition(this.camWorld);
        // THE FRAME LAW (the waterfall's lesson): GPU kernels spawn in RENDER
        // space (the mesh transform is ignored), but the CPU adapter's instances
        // are children of the scene and take ABSOLUTE coordinates — the render
        // point minus scene.position. Seated in render space, the WebGL 2 rain
        // fell 500 km from the camera and every headless picture was dry.
        this.camAbs.copy(this.camWorld).sub(this.scene.position); // absolute, from the RENDER-space camera, whatever the emitter backend
        if (this.near?.backend === 'cpu') this.camWorld.sub(this.scene.position);
        const wx = this.windDir.x, wz = this.windDir.y;
        if (this.emitterKind === 'dust') {
          // The dust streams PAST the camera along the wind: both boxes sit
          // UPWIND and their +Y (the spawn direction) points down the wind, a
          // hair downward, so the grains sweep through the player's air.
          this.fxDir.set(wx, -0.08, wz).normalize();
          this.fxQuat.setFromUnitVectors(this.fxUp, this.fxDir);
          this.fxPos.set(this.camWorld.x - wx * VOLUME.dust.nearUpwind, this.camWorld.y + VOLUME.dust.nearUp, this.camWorld.z - wz * VOLUME.dust.nearUpwind);
          this.near?.worldMatrix.compose(this.fxPos, this.fxQuat, this.fxScale);
          this.fxPos.set(this.camWorld.x - wx * VOLUME.dust.farUpwind, this.camWorld.y + VOLUME.dust.farUp, this.camWorld.z - wz * VOLUME.dust.farUpwind);
          this.mid?.worldMatrix.compose(this.fxPos, this.fxQuat, this.fxScale);
          // The saltation layer hugs the ground: a knee-high box upwind of the feet.
          this.fxPos.set(this.camWorld.x - wx * VOLUME.dust.saltUpwind, this.camWorld.y + VOLUME.dust.saltUp, this.camWorld.z - wz * VOLUME.dust.saltUpwind);
          this.salt?.worldMatrix.compose(this.fxPos, this.fxQuat, this.fxScale);
          // The haze shells ride the camera (scene children: ABSOLUTE coordinates).
          // WHY camAbs: on the CPU (WebGL 2) backend camWorld is ALREADY absolute
          // here, so subtracting the origin again put the shells at abs + origin —
          // hundreds of km away past the first rebase: no haze in the dunes (2026-09-12).
          for (const sh of this.dustShells) sh.position.copy(this.camAbs);
        } else {
          // Volumes sit ABOVE the player ("rain falls from the sky, not from
          // head height") — drops spawn falling at terminal speed (negative
          // startSpeed along the volume's +Y) and cross the eye line already
          // streaking. The WIND SLANTS the volume: its +Y leans upwind, so the
          // fall (−Y) drifts downwind — rain a little, snow a lot. Heights and
          // lifetimes are paired in lib/weather_fx.ts so every drop reaches the
          // ground before the pool recycles it.
          const slant = precipSlant(this.emitterKind, this.windStrength); // ONE source of truth with the shelter bake (rain_shelter.ts)
          this.fxDir.set(-wx * slant, 1, -wz * slant).normalize();
          this.fxQuat.setFromUnitVectors(this.fxUp, this.fxDir);
          const up = this.emitterKind === 'snow' ? VOLUME.snow : VOLUME.rain;
          this.fxPos.set(this.camWorld.x, this.camWorld.y + up.near, this.camWorld.z);
          this.near?.worldMatrix.compose(this.fxPos, this.fxQuat, this.fxScale);
          this.fxPos.set(this.camWorld.x, this.camWorld.y + up.mid, this.camWorld.z);
          this.mid?.worldMatrix.compose(this.fxPos, this.fxQuat, this.fxScale);
          // THE SPLASH SEAT (physics follow-up A, lib/weather_fx.ts splashSeatY): on max(ground, still water) under the
          // camera — or the deck / roof the player stands on — never a fixed 1.55 m under the camera. Judged in ABSOLUTE
          // coordinates (camAbs), then carried into the emitter's frame by the camera's own offset between the two
          // (zero on the CPU backend, the scene shift on the GPU one — THE FRAME LAW, without a branch).
          if (this.splash) {
            const seat = this.splashSeat();
            this.splash.worldMatrix.setPosition(this.camWorld.x, this.camWorld.y + (seat - this.camAbs.y), this.camWorld.z);
          }
        }
      }
      // A hitch must not age every drop out in one step (the CPU emitter has no clamp of its own).
      const pdt = Math.min(0.1, Math.max(0, dt));
      this.near?.update(pdt);
      this.mid?.update(pdt);
      this.splash?.update(pdt);
      this.salt?.update(pdt);
      // weather surfaces R3: the WebGL2 CPU drops under a roof are gated from the CPU copy of the roof map
      if (this.near?.backend === 'cpu' || this.salt?.backend === 'cpu') gateCpuEmitters([this.near, this.mid, this.splash, this.salt] as never);
      // THE CPU BACKEND DRAWS ONLY WHEN TOLD (coordinator QA, 2026-09-26): a CpuParticleAdapter simulates in
      // update() but its InstancedMesh is filled by render(camera) — the river, water, fire and underwater
      // emitters all call it; this module never did, so on WebGL2 every weather drop was simulated and never
      // drawn (the 2026-09-11 "268 drops" gate read a counter, not a picture). Billboarding takes the
      // RENDER-space camera; the instances are already seated absolute (the FRAME LAW above).
      if (cam) for (const e of [this.near, this.mid, this.splash, this.salt]) if (e && e.backend === 'cpu') (e as CpuParticleAdapter).render(cam);
    }
  }

  /** The splash slab's absolute seat under the camera, resampled when the camera moved 2 m (an analytic ground sample
   *  is tens of µs — not every frame). */
  private splashSeat(): number {
    const x = this.camAbs.x, z = this.camAbs.z;
    if (!(Math.abs(x - this.seatAt.x) < 2 && Math.abs(z - this.seatAt.z) < 2)) {
      this.seatAt.x = x; this.seatAt.z = z;
      const conn = atlas.conn;
      this.seatAt.ground = conn && atlas.currentWorldId !== null ? groundYIn(conn, atlas.currentWorldId, x, z) : null;
      this.seatAt.water = waterStillAt(x, z);
    }
    const onFoot = !atlas.hoverRide && !atlas.skyFlight && !atlas.spaceFlight && atlas.interiorId === null;
    const camToFeetM = Math.hypot(x - atlas.localX, z - atlas.localZ);
    const y = splashSeatY({ ground: this.seatAt.ground, water: this.seatAt.water, camY: this.camAbs.y, feet: atlas.localY, onFoot, camToFeetM });
    this.seatAt.y = y;
    return y;
  }
  private readonly seatAt: { x: number; z: number; ground: number | null; water: number | null; y: number } = { x: NaN, z: NaN, ground: null, water: null, y: NaN };

  /**
   * Returns the terrainSeed for the 'commons' world — the biome resolver's seed.
   * The seed is CONSTANT, so resolve it once (via the cached commons world id, an
   * indexed find) and memoise — this runs every RenderSub frame from update().
   */
  private commonsSeed(conn: DbConnection): string {
    if (this.cachedSeed !== null) return this.cachedSeed;
    const id = atlas.commonsWorldId;
    if (id !== null) { const w = conn.db.world.worldId.find(id); if (w) return (this.cachedSeed = w.terrainSeed); }
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') return (this.cachedSeed = w.terrainSeed);
    return 'commons'; // not replicated yet — don't memoise the fallback
  }

  /**
   * Returns the live-ops override string when skyState.weather is set and not 'clear'.
   * Called every RenderSub frame: use the cached commons world id for an O(1) indexed
   * skyState lookup; the full world scan is kept only as a pre-replication fallback.
   */
  private commonsWeatherOverride(conn: DbConnection): string | undefined {
    const id = atlas.commonsWorldId;
    if (id !== null) {
      const s = conn.db.skyState.worldId.find(id);
      const wx = s?.weather;
      return wx && wx !== 'clear' ? wx : undefined;
    }
    for (const w of conn.db.world.iter()) {
      if (w.kind === 'commons') {
        const s = conn.db.skyState.worldId.find(w.worldId);
        const wx = s?.weather;
        return wx && wx !== 'clear' ? wx : undefined;
      }
    }
    return undefined;
  }

  /** Inside a building or a ship interior — walls muffle the weather. */
  private indoors(): boolean { const id = atlas.interiorId; return typeof id === 'string' && id.length > 0; }

  /** One lightning flash now (a storm hazard's near miss, 2026-09-24) — the flash light's usual rising edge. */
  strikeFlash(): void { this.forceFlash = true; }

  /** Test/inspection hooks. */
  get kind(): string { return this.emitterKind; }
  get activeParticles(): number { return (this.near?.getActiveCount() ?? 0) + (this.mid?.getActiveCount() ?? 0) + (this.salt?.getActiveCount() ?? 0); }
  get hasEmitter(): boolean { return this.near !== null || this.mid !== null; }
  get emissionRate(): number { return this.hasEmitter ? this.rate() : 0; }
  get wetnessLevel(): number { return getWetness(); }
  get windLevel(): number { return windVector.z; }
  /** The wind + weather the water reads (interactive water, 2026-09-11): unit direction, strength 0..1, the precipitation kind + density. */
  wind(): { x: number; z: number; strength: number; kind: string; density: number; targetKind: string; targetDensity: number; temperatureC: number | null } {
    // The TARGET is the resolver's answer (immediate — a pin or the natural
    // field); the live kind/density lerp toward it on the frame clock. Ambient
    // systems that must not lag the sky (the insects) read the target.
    return {
      x: this.windDir.x, z: this.windDir.y, strength: this.windStrength, kind: this.emitterKind, density: this.density,
      targetKind: this.resolveTarget?.particleKind ?? this.emitterKind, targetDensity: this.resolveTarget?.particleDensity ?? this.density,
      temperatureC: this.lastMeteo?.temperatureC ?? null,
    };
  }
  /** The meteorological readout at the player (debug menu / diagnostics). */
  get meteo(): Meteo | null { return this.lastMeteo; }
  /** Ground snow cover 0..1 as the weather sees it (the debug pin wins) — footprints pick the snow decal on it. */
  get snowLevel(): number { return this.debugSnow ?? this.snowiness; }
  applyForTest(weather: string): void { const conn = atlas.conn; if (conn) this.applyWeather(weather, conn); }
  stepForTest(dt: number): void { this.update(dt); }

  dispose(): void { this.disposeEmitters(); }
}

// ─── Module-scope helpers ─────────────────────────────────────────────────────

/** Convert a '#rrggbb' hex string to normalised 0..1 RGB for the cloud renderer. */
function hexToRgb01(hex: string): { r: number; g: number; b: number } {
  const n = parseInt(hex.slice(1), 16);
  return { r: ((n >> 16) & 255) / 255, g: ((n >> 8) & 255) / 255, b: (n & 255) / 255 };
}
